import * as THREE from 'three';
import { Line, Mesh, Material } from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { ITyphoon, Forecast} from '../../../shared/models/JTWC';
  
interface PathPoint {
    vector: THREE.Vector3;
    time: string;
    windSpeed: number;
    date?: Date;  // 実際の日時を追加
}

export class TyphoonVisualizer {
  private scene: THREE.Scene;
  private objects: (THREE.Mesh | THREE.Line | CSS2DObject)[] = []; 

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public update(data: ITyphoon | null, options: {
    showWindRadii?: boolean;
    showForecastPath?: boolean;
    pathColor?: string;
    windRadiiOpacity?: number;
  } = {}) {
    this.cleanup();
    if (!data) return;
  
    if (options.showForecastPath) {
      this.drawPath(data, options.pathColor || '#ff0000');
    }
  
    if (options.showWindRadii) {
      this.drawWindRadii(data, options.windRadiiOpacity || 0.3);
    }
  }

  private drawPath(typhoon: ITyphoon, color: string) {
    const points: PathPoint[] = [];
    // TypeGuardを使用して時刻の存在を確認
    const baseTime = typhoon.currentPosition.time || new Date().toISOString();
    const baseDate = new Date(baseTime);

    // 現在位置を追加
    points.push({
      vector: this.latLngToVector3(
        typhoon.currentPosition.lat,
        typhoon.currentPosition.lon,
        5.6
      ),
      time: '現在',
      windSpeed: typhoon.forecasts[0]?.windSpeed || 0,
      date: baseDate
    });
  
    // 予報位置を追加
    typhoon.forecasts.forEach((forecast: Forecast) => {
      const forecastTime = typeof forecast.time === 'string' ? 
        parseInt(forecast.time, 10) : forecast.time;
      const forecastDate = new Date(baseDate.getTime() + forecastTime * 3600000);
      
      points.push({
        vector: this.latLngToVector3(
          forecast.position.lat,
          forecast.position.lon,
          5.6
        ),
        time: `+${forecast.time}h`,
        windSpeed: forecast.windSpeed,
        date: forecastDate
      });
    });
  
    // 経路線を描画（シンプルな線で表現）
    const lineGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(points.length * 3);
    
    points.forEach((point, i) => {
      positions[i * 3] = point.vector.x;
      positions[i * 3 + 1] = point.vector.y;
      positions[i * 3 + 2] = point.vector.z;
    });
    
    lineGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const lineMaterial = new THREE.LineBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: 0.8,
      linewidth: 2,
      depthTest: true,
      depthWrite: false
    });
    
    const line = new THREE.Line(lineGeometry, lineMaterial);
    line.renderOrder = 2001;

    // カメラからのオブジェクトの可視性をチェックする関数
    const checkVisibility = (camera: THREE.Camera, position: THREE.Vector3) => {
      const cameraDirection = new THREE.Vector3();
      camera.getWorldDirection(cameraDirection);
            
      // 地球の中心から対象への方向ベクトル
      const centerToObject = position.clone().normalize();
      
      // 地球の中心からカメラへの方向ベクトル
      const centerToCamera = camera.position.clone().normalize();
      
      // 角度を計算
      const angle = centerToObject.angleTo(centerToCamera);
      
      // 裏側にある場合は非表示（90度以上で裏側と判定）
      return angle < Math.PI / 2;
    };

    // 経路線の可視性制御
    /*line.onBeforeRender = (renderer, scene, camera) => {
      // 各頂点の可視性をチェック
      let anyVisible = false;
      points.forEach(point => {
        if (checkVisibility(camera, point.vector)) {
          anyVisible = true;
        }
      });
      line.visible = anyVisible;
    };*/
    this.scene.add(line);
    this.objects.push(line);
  
    // 各ポイントにマーカーと時刻を表示
    points.forEach((point, index) => {
      // 間引き処理（最初、最後、3ポイントごとに表示）
      if (index !== 0 && index !== points.length - 1 && index % 3 !== 0) return;

        // 風速に基づいて色を決定
      const intensity = point.windSpeed / 150;
      const markerColor = new THREE.Color().setHSL(0, 1, 0.7 - intensity * 0.4);
  
      // マーカー
      const markerGeometry = new THREE.SphereGeometry(0.08, 16, 16);
      const markerMaterial = new THREE.MeshPhongMaterial({
        color: markerColor,
        transparent: true,
        opacity: 0.9,
        shininess: 100,
        depthWrite: false
      });
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      marker.position.copy(point.vector);

      marker.renderOrder = 2002; 
      //marker.onBeforeRender = (renderer, scene, camera) => {
      //  marker.visible = checkVisibility(camera, point.vector);
      //};
      this.scene.add(marker);
      this.objects.push(marker);

      // 時刻ラベルの改善
      const labelDiv = document.createElement('div');
      labelDiv.className = 'typhoon-time-label';

        // 日時表示の改善
        const dateStr = point.date?.toLocaleString('ja-JP', {
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            }) || point.time;

            labelDiv.innerHTML = `

        <div style="
          background-color: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-family: Arial, sans-serif;
          pointer-events: none;
          white-space: nowrap;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        ">
          <span style="font-weight: bold; font-size: 12px;">${dateStr}</span>
          <span style="font-size: 11px; opacity: 0.8;">${point.windSpeed}kt</span>
        </div>
      `;
  
      const label = new CSS2DObject(labelDiv);
      const labelPosition = point.vector.clone().multiplyScalar(1.08);
      label.position.copy(labelPosition);
      label.renderOrder = 2003;

      label.onBeforeRender = (renderer, scene, camera) => {
        label.visible = checkVisibility(camera, point.vector);
      };
      this.scene.add(label);
      this.objects.push(label);

      
    });
  }

  private drawWindRadii(typhoon: ITyphoon, opacity: number) {
    typhoon.forecasts.forEach((forecast: Forecast) => {
      if (!forecast.windRadii) return;
  
      const position = this.latLngToVector3(
        forecast.position.lat,
        forecast.position.lon,
        5.2
      );
  
      // 風速帯ごとに高度を少しずつ変える（さらに小さな値に）
      const heightOffsets = {
        r064: 0.002,  // より小さな値に調整
        r050: 0.001,
        r034: 0.0005
      };
  
      Object.entries(forecast.windRadii).forEach(([key, radii]) => {
        if (!radii) return;
  
        // 色の調整（より薄く、見やすい色に）
        const color = key === 'r064' ? 0xFF9999 :  // より薄い赤に
                     key === 'r050' ? 0xFFBBBB :  // さらに薄い赤に
                     0xFFDDDD;                    // 最も薄い赤に
  
        // スケーリング係数をさらに小さく
        const scaleFactor = 0.0002;  // さらに小さな値に調整
        const avgRadius = this.calculateAverageRadius(radii) * scaleFactor;
  
        const geometry = new THREE.RingGeometry(
          avgRadius * 0.98,  // 内側の半径
          avgRadius,         // 外側の半径
          32,               // セグメント数
          1
        );
  
        const material = new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: key === 'r064' ? 0.15 :    // 不透明度をさらに下げる
                   key === 'r050' ? 0.1 :
                   0.05,
          side: THREE.DoubleSide,
          depthTest: true,
          depthWrite: false
        });
  
        const mesh = new THREE.Mesh(geometry, material);
        
        // 位置を風速帯ごとに少しずつずらす
        const adjustedPosition = position.clone().add(
          new THREE.Vector3(0, 0, heightOffsets[key as keyof typeof heightOffsets])
        );
        
        mesh.position.copy(adjustedPosition);
        mesh.lookAt(new THREE.Vector3(0, 0, 0));
  
        this.scene.add(mesh);
        this.objects.push(mesh);
      });
    });
  }

  private calculateAverageRadius(radii: { ne: number | null; se: number | null; sw: number | null; nw: number | null; }): number {
    const values = [radii.ne, radii.se, radii.sw, radii.nw].filter(v => v !== null) as number[];
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  }

  private latLngToVector3(lat: number, lon: number, radius: number): THREE.Vector3 {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);
    const x = -(radius * Math.sin(phi) * Math.cos(theta));
    const z = radius * Math.sin(phi) * Math.sin(theta);
    const y = radius * Math.cos(phi);
    return new THREE.Vector3(x, y, z);
  }

  public cleanup() {
    this.objects.forEach(obj => {
      this.scene.remove(obj);
      if (obj instanceof Mesh) {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material instanceof Material) {
          obj.material.dispose();
        }
      } else if (obj instanceof Line) {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material instanceof Material) {
          obj.material.dispose();
        }
      }
    });
    this.objects = [];
  }

  // カメラからポイントが見えるか確認
private isPointVisible(vector: THREE.Vector3): boolean {
  const camera = this.scene.userData.camera as THREE.Camera;
  const frustum = new THREE.Frustum();
  const cameraViewProjectionMatrix = new THREE.Matrix4();
  camera.updateMatrixWorld();
  cameraViewProjectionMatrix.multiplyMatrices(
    camera.projectionMatrix,
    camera.matrixWorldInverse
  );
  frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);

  // 視錐台に入っているか判定
  return frustum.containsPoint(vector);
}

// THREE.jsのベクトルをスクリーン座標に変換
private toScreenPosition(vector: THREE.Vector3): THREE.Vector2 {
  const camera = this.scene.userData.camera as THREE.Camera;
  const widthHalf = window.innerWidth / 2;
  const heightHalf = window.innerHeight / 2;

  vector.project(camera);
  return new THREE.Vector2(
    vector.x * widthHalf + widthHalf,
    -(vector.y * heightHalf) + heightHalf
  );
}
}